// Full-page style preview that opens below the mobile header.
// Layout: side-by-side photo+template, inline photo tips, camera/gallery upload,
// optional vibe mode toggle, generate CTA.
import { Camera, Image as ImageIcon, Wand2, AlertTriangle, User, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PhotoTips } from "@/components/PhotoTips";
import { cn } from "@/lib/utils";
import { useEffect } from "react";

type Kind = "vibe" | "look" | "item";

interface Props {
  open: boolean;
  onClose: () => void;
  kind: Kind;
  template: {
    label: string;
    image: string;
    description?: string;
    prompt?: string;
  } | null;
  userPhotoPreview: string | null;
  onPickPhoto: (file: File | undefined) => void;
  onConfirm: () => void;
  vibeMode?: "scene" | "outfit";
  setVibeMode?: (m: "scene" | "outfit") => void;
}

export const StylePreviewSheet = ({
  open, onClose, kind, template, userPhotoPreview,
  onPickPhoto, onConfirm, vibeMode, setVibeMode,
}: Props) => {
  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!template || !open) return null;

  const ctaLabel =
    kind === "look" ? "Try this look · 1 credit"
    : kind === "item" ? "Try this on me · 1 credit"
    : "Generate · 1 credit";

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex flex-col bg-background animate-fade-in"
        role="dialog"
        aria-modal="true"
      >
        {/* Sticky header — sits at top, below safe area */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-white/5 bg-background/90 px-4 pt-[max(env(safe-area-inset-top),12px)] pb-3 backdrop-blur-xl">
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-full bg-card text-foreground active:scale-95"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
          <h2 className="flex-1 truncate text-center text-[15px] font-semibold">
            {template.label}
          </h2>
          <div className="h-9 w-9" />
        </div>

        {/* Scrollable body */}
        <div className="no-scrollbar flex-1 overflow-y-auto overscroll-contain px-4 pb-32 pt-4">
          <div className="mx-auto max-w-md space-y-4">
            {/* Side-by-side photo + template */}
            <div className="grid grid-cols-2 gap-2">
              <div className="relative overflow-hidden rounded-2xl bg-muted ring-1 ring-white/10">
                {userPhotoPreview ? (
                  <img src={userPhotoPreview} alt="You" className="aspect-[4/5] w-full object-cover" />
                ) : (
                  <div className="flex aspect-[4/5] w-full flex-col items-center justify-center gap-2 bg-gradient-to-br from-primary/15 to-accent/10 p-3 text-center">
                    <div className="grid h-10 w-10 place-items-center rounded-full bg-primary/20 text-primary">
                      <User className="h-5 w-5" />
                    </div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Add photo
                    </p>
                  </div>
                )}
                <div className="absolute left-1.5 top-1.5 rounded-full bg-black/70 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white">
                  You
                </div>
              </div>
              <div className="relative overflow-hidden rounded-2xl bg-muted ring-1 ring-white/10">
                <img
                  src={template.image}
                  alt={template.label}
                  className={cn(
                    "aspect-[4/5] w-full",
                    kind === "item" ? "object-contain p-3" : "object-cover",
                  )}
                />
                <div className="absolute left-1.5 top-1.5 rounded-full bg-primary/90 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-primary-foreground">
                  Template
                </div>
              </div>
            </div>

            {/* Step 1 — only show when no photo yet */}
            {!userPhotoPreview && (
              <div className="rounded-2xl border border-primary/30 bg-primary/10 p-3">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-primary">
                  Step 1 · Add your photo
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex h-12 cursor-pointer items-center justify-center gap-2 rounded-full bg-foreground text-background text-[12px] font-semibold active:scale-95">
                    <input
                      type="file" accept="image/*" capture="environment" className="hidden"
                      onChange={(e) => onPickPhoto(e.target.files?.[0])}
                    />
                    <Camera className="h-4 w-4" /> Camera
                  </label>
                  <label className="flex h-12 cursor-pointer items-center justify-center gap-2 rounded-full border border-white/15 bg-card text-[12px] font-semibold active:scale-95">
                    <input
                      type="file" accept="image/*" className="hidden"
                      onChange={(e) => onPickPhoto(e.target.files?.[0])}
                    />
                    <ImageIcon className="h-4 w-4" /> Gallery
                  </label>
                </div>
              </div>
            )}

            {/* Photo tips — always visible inline */}
            <PhotoTips />

            {/* Optional template description / prompt */}
            {(template.description || template.prompt) && (
              <p className="text-[12px] leading-relaxed text-muted-foreground">
                {template.description ?? template.prompt}
              </p>
            )}

            {/* What changes? — vibes only */}
            {kind === "vibe" && setVibeMode && (
              <div>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  What changes?
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <ModeOption
                    active={vibeMode === "scene"} onClick={() => setVibeMode("scene")}
                    title="Scene only" sub="Same outfit"
                  />
                  <ModeOption
                    active={vibeMode === "outfit"} onClick={() => setVibeMode("outfit")}
                    title="Scene + outfit" sub="Restyle"
                  />
                </div>
              </div>
            )}

            {/* Swap photo (when one is already loaded) */}
            {userPhotoPreview && (
              <label className="flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-full border border-white/15 bg-card text-[13px] font-semibold text-foreground active:scale-95">
                <input
                  type="file" accept="image/*" className="hidden"
                  onChange={(e) => onPickPhoto(e.target.files?.[0])}
                />
                <Camera className="h-4 w-4" /> Change photo
              </label>
            )}
          </div>
        </div>

        {/* Sticky CTA at bottom */}
        <div className="sticky bottom-0 border-t border-white/5 bg-background/95 px-4 pb-[max(env(safe-area-inset-bottom),12px)] pt-3 backdrop-blur-xl">
          <div className="mx-auto max-w-md space-y-2">
            <Button
              className="h-12 w-full rounded-full gradient-primary text-primary-foreground disabled:opacity-50"
              disabled={!userPhotoPreview}
              onClick={onConfirm}
            >
              <Wand2 className="mr-2 h-4 w-4" /> {ctaLabel}
            </Button>
            <p className="flex items-center justify-center gap-1.5 text-center text-[11px] text-muted-foreground">
              <AlertTriangle className="h-3 w-3 shrink-0" />
              AI may be inaccurate — use as inspiration.
            </p>
          </div>
        </div>
      </div>

    </>
  );
};

const ModeOption = ({ active, onClick, title, sub }: { active: boolean; onClick: () => void; title: string; sub: string }) => (
  <button
    type="button" onClick={onClick}
    className={cn(
      "rounded-2xl border p-3 text-left transition-all active:scale-[0.98]",
      active ? "border-primary bg-primary/10" : "border-white/10 bg-transparent",
    )}
  >
    <p className="text-[13px] font-semibold leading-tight">{title}</p>
    <p className="mt-0.5 text-[11px] text-muted-foreground">{sub}</p>
  </button>
);
